/**
 * Utility functions for handling SCIM reference types
 * Based on analysis of schemas.json reference types
 */

/**
 * Reference type mappings from schemas.json analysis
 * Maps field names to their expected reference target types
 */
export const REFERENCE_TYPE_MAPPINGS = {
    // Person schema reference types
    'EntityType': ['SchemaEntitiy'],
    'Manager': ['Contractor', 'Employee', 'Sponsored', 'Vacancy'],
    'Manages': ['Contractor', 'Employee', 'Shared', 'Sponsored', 'Vacancy'],
    'MemberOf': ['Group'],
    'MemberOfTransitive': ['Group'],
    'School': ['School'],
    'Schools': ['School'],
    'Rc': ['ResponsibilityCenter'],
    'Rcs': ['ResponsibilityCenter'],
    'Classes': ['Class'],
    'Teachers': ['Contractor', 'Employee', 'Sponsored', 'Vacancy'],
    'Students': ['Student'],
    'Sponsor': ['Employee'],
    'Sponsors': ['Sponsored'],
    'Guardians': ['Public'],
    'PerpetualAccess': ['Contractor', 'Employee', 'Sponsored', 'Vacancy'],
    'WeekendAccess': ['Building', 'Contractor', 'Employee', 'Sponsored', 'Vacancy'],
    'WorkdayAccess': ['Building', 'Contractor', 'Employee', 'Sponsored', 'Vacancy'],
    'BuildingAssistant': ['Employee'],
    'BuildingManager': ['Employee'],
    'BusStops': ['StudentBusStop'],
    'PrincipalOf': ['ResponsibilityCenter'],
    'SchoolPrincipalOf': ['School'],

    // Group schema reference types
    'Member': ['Group', 'Person'],
    'MemberTransitive': ['Group', 'Person'],

    // Other common reference types
    'Building': ['Building'],
    'Class': ['Class'],
    'ResponsibilityCenter': ['ResponsibilityCenter'],
    'StudentBusStop': ['StudentBusStop'],
    'Public': ['Public'],
    'Employee': ['Employee'],
    'Contractor': ['Contractor'],
    'Sponsored': ['Sponsored'],
    'Student': ['Student'],
    'Applicant': ['Applicant'],
    'Vacancy': ['Vacancy'],
    'Shared': ['Shared']
} as const;

/**
 * Common reference object property patterns
 * Ordered by priority for extraction
 */
const REFERENCE_PROPERTY_PATTERNS = [
    'DisplayName',    // Most common
    'displayName',    // camelCase variant
    'display',        // Alternative display property
    'name',           // Generic name property
    'value',          // Generic value property
    'Id',             // ID property
    'id',             // camelCase ID
    'UserName',       // Username property
    'userName',       // camelCase username
    'Email',          // Email property
    'email',          // camelCase email
    'Title',          // Title property
    'title',          // camelCase title
    'Description',    // Description property
    'description'     // camelCase description
] as const;

/**
 * Extracts a displayable string from a SCIM reference object
 * @param value - The reference object or primitive value
 * @param fieldName - The field name for context-specific handling
 * @returns A displayable string representation
 */
export function extractReferenceValue(value: any, fieldName?: string): string {
    if (value === null || value === undefined) {
        return '-';
    }

    // If it's already a primitive type, return as string
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        return String(value);
    }

    // If it's an array, handle it
    if (Array.isArray(value)) {
        if (value.length === 0) return '[]';
        // For arrays, show the first item or count
        return value.length === 1 ? extractReferenceValue(value[0], fieldName) : `[${value.length} items]`;
    }

    // If it's an object, try to extract meaningful information
    if (typeof value === 'object') {
        // Special handling for specific field types
        if (fieldName) {
            const extracted = extractFieldSpecificValue(value, fieldName);
            if (extracted !== null) {
                return extracted;
            }
        }

        // Try common reference object patterns
        for (const pattern of REFERENCE_PROPERTY_PATTERNS) {
            if (value[pattern] && typeof value[pattern] === 'string') {
                return value[pattern];
            }
        }

        // Handle nested name objects (common in SCIM)
        if (value.name && typeof value.name === 'object') {
            if (value.name.formatted) return value.name.formatted;
            if (value.name.givenName && value.name.familyName) {
                return `${value.name.givenName} ${value.name.familyName}`;
            }
            if (value.name.givenName) return value.name.givenName;
            if (value.name.familyName) return value.name.familyName;
        }

        // Handle email objects
        if (value.emails && Array.isArray(value.emails) && value.emails.length > 0) {
            const primaryEmail = value.emails.find((email: any) => email.primary);
            if (primaryEmail && primaryEmail.value) return primaryEmail.value;
            if (value.emails[0] && value.emails[0].value) return value.emails[0].value;
        }

        // If it has a single string property, use that
        const stringProps = Object.keys(value).filter(key => typeof value[key] === 'string');
        if (stringProps.length === 1) {
            return value[stringProps[0]];
        }

        // For complex objects, show a summary
        const keys = Object.keys(value);
        if (keys.length === 0) return '{}';
        return `{${keys.slice(0, 2).join(', ')}${keys.length > 2 ? '...' : ''}}`;
    }

    return '-';
}

/**
 * Field-specific value extraction for known reference types
 * @param value - The reference object
 * @param fieldName - The field name
 * @returns Extracted value or null if no specific handling
 */
function extractFieldSpecificValue(value: any, fieldName: string): string | null {
    switch (fieldName) {
        case 'EntityType':
            // Handle SCIM reference object structure: {DisplayName, Id, EntityType, $ref}
            if (value.DisplayName) return value.DisplayName;
            if (value.Id) return value.Id;
            if (value.display) return value.display;
            if (value.value) return value.value;
            if (value.name) return value.name;
            if (value.type) return value.type;
            if (value.entityType) return value.entityType;
            break;

        case 'Manager':
        case 'Manages':
        case 'Teachers':
        case 'Students':
        case 'Sponsor':
        case 'Sponsors':
        case 'Guardians':
        case 'PerpetualAccess':
        case 'WeekendAccess':
        case 'WorkdayAccess':
        case 'BuildingAssistant':
        case 'BuildingManager':
        case 'PrincipalOf':
        case 'SchoolPrincipalOf':
            // Person/Employee references - prioritize DisplayName, then UserName, then Id
            if (value.DisplayName) return value.DisplayName;
            if (value.UserName) return value.UserName;
            if (value.UserPrincipalName) return value.UserPrincipalName;
            if (value.Id) return value.Id;
            break;

        case 'School':
        case 'Schools':
            // School references
            if (value.DisplayName) return value.DisplayName;
            if (value.SchoolName) return value.SchoolName;
            if (value.Name) return value.Name;
            if (value.Id) return value.Id;
            break;

        case 'Rc':
        case 'Rcs':
        case 'PrincipalOf':
            // ResponsibilityCenter references
            if (value.DisplayName) return value.DisplayName;
            if (value.Name) return value.Name;
            if (value.Id) return value.Id;
            break;

        case 'Classes':
            // Class references
            if (value.DisplayName) return value.DisplayName;
            if (value.ClassName) return value.ClassName;
            if (value.Course) return value.Course;
            if (value.Subject) return value.Subject;
            if (value.Id) return value.Id;
            break;

        case 'MemberOf':
        case 'MemberOfTransitive':
        case 'Member':
        case 'MemberTransitive':
            // Group references
            if (value.DisplayName) return value.DisplayName;
            if (value.Name) return value.Name;
            if (value.Description) return value.Description;
            if (value.Id) return value.Id;
            break;

        case 'Building':
        case 'WeekendAccess':
        case 'WorkdayAccess':
            // Building references
            if (value.DisplayName) return value.DisplayName;
            if (value.BuildingName) return value.BuildingName;
            if (value.Name) return value.Name;
            if (value.Address) return value.Address;
            if (value.Id) return value.Id;
            break;

        case 'BusStops':
            // StudentBusStop references
            if (value.DisplayName) return value.DisplayName;
            if (value.Location) return value.Location;
            if (value.Route) return value.Route;
            if (value.Id) return value.Id;
            break;
    }

    return null;
}

/**
 * Checks if a field is a known reference type
 * @param fieldName - The field name to check
 * @returns True if the field is a known reference type
 */
export function isReferenceType(fieldName: string): boolean {
    return fieldName in REFERENCE_TYPE_MAPPINGS;
}

/**
 * Gets the expected reference target types for a field
 * @param fieldName - The field name
 * @returns Array of expected reference target types
 */
export function getReferenceTargetTypes(fieldName: string): readonly string[] {
    return REFERENCE_TYPE_MAPPINGS[fieldName as keyof typeof REFERENCE_TYPE_MAPPINGS] || [];
}

/**
 * Enhanced version of renderEntityProperty that uses the comprehensive reference handling
 * @param value - The value to render
 * @param fieldName - The field name for context
 * @returns A displayable string representation
 */
export function renderEntityProperty(value: any, fieldName: string): string {
    return extractReferenceValue(value, fieldName);
}
